import { useEffect, useState } from 'react';
import type { CommandDashboard, FlagPriority, ZoneStatusCard } from '@vigilo/shared';
import { get } from '@vigilo/api-client';

export interface MovementTrendBucket {
  bucketStart: string;
  inflow: number;
  outflow: number;
  netFlow: number;
}

export type MetricsFreshnessStatus = 'fresh' | 'stale' | 'missing';

export interface CommandDashboardCrowdLoadTrendBucket {
  bucketStart: string;
  crowdLoadIndex: number | null;
}

export interface CommandDashboardCrowdLoadTrend {
  windowMinutes: number;
  buckets: CommandDashboardCrowdLoadTrendBucket[];
}

export type CommandDashboardMovementTrend = CommandDashboard['movementTrend'] & {
  buckets: MovementTrendBucket[];
  freshnessStatus?: MetricsFreshnessStatus;
  freshnessAgeMs?: number | null;
  freshnessWindowMs?: number;
  freshnessEvaluatedAt?: string;
  latestSampledAt?: string | null;
};

export interface CommandDashboardZoneStatusCard extends ZoneStatusCard {
  capacity: number;
  occupancyCount?: number | null;
  freshnessStatus?: MetricsFreshnessStatus;
  freshnessAgeMs?: number | null;
  freshnessWindowMs?: number;
  freshnessEvaluatedAt?: string;
}

export interface CommandDashboardTeamSummary {
  teamId: string;
  name: string;
  color: string | null;
  zoneIds: string[];
  zoneCount: number;
  analystCount: number;
  memberUserIds: string[];
  alertCount: number;
  isDefault: boolean;
}

export type CommandDashboardFlagActivityStatus = 'active_in_zone' | 'recent_activity' | 'no_activity';

export interface CommandDashboardFlagRow {
  flagId: string;
  tokenRef: string;
  priority: FlagPriority;
  behaviourNote: string;
  monitoredZoneIds: string[];
  createdAt: string;
  expiresAt: string | null;
  currentZoneId: string | null;
  currentZoneName: string | null;
  latestActivityAt: string | null;
  activityStatus: CommandDashboardFlagActivityStatus;
}

export type CommandDashboardProjection = Omit<CommandDashboard, 'movementTrend' | 'zoneStatusCards'> & {
  operationId: string;
  flagSummary: {
    active: number;
    urgent: number;
    limit: number;
  };
  flagRows: CommandDashboardFlagRow[];
  teamSummary: CommandDashboardTeamSummary[];
  crowdLoadTrend: CommandDashboardCrowdLoadTrend;
  movementTrend: CommandDashboardMovementTrend;
  zoneStatusCards: CommandDashboardZoneStatusCard[];
};

interface CommandDashboardState {
  dashboard: CommandDashboardProjection | null;
  loading: boolean;
  error: string | null;
}

function unwrap(body: CommandDashboardProjection | { data?: CommandDashboardProjection }): CommandDashboardProjection | null {
  return 'data' in (body as { data?: CommandDashboardProjection })
    ? (body as { data?: CommandDashboardProjection }).data ?? null
    : body as CommandDashboardProjection;
}

export function useCommandDashboard(
  operationId?: string | null,
  enabled = true,
  pollMs = 2000,
): CommandDashboardState {
  const [dashboard, setDashboard] = useState<CommandDashboardProjection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !operationId) {
      setDashboard(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    let timer: number | undefined;
    let inFlight = false;

    async function load() {
      if (inFlight) return;
      inFlight = true;
      setLoading(true);
      try {
        const qs = `?operationId=${encodeURIComponent(operationId as string)}`;
        const body = await get<CommandDashboardProjection | { data?: CommandDashboardProjection }>(`/command-reporting/dashboard${qs}`);
        if (!cancelled) {
          setDashboard(unwrap(body));
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load command dashboard');
      } finally {
        if (!cancelled) setLoading(false);
        inFlight = false;
      }
    }

    function scheduleNext() {
      if (cancelled) return;
      timer = window.setTimeout(() => {
        void load().finally(scheduleNext);
      }, pollMs);
    }

    void load().finally(scheduleNext);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [enabled, operationId, pollMs]);

  return { dashboard, loading, error };
}
